import React, { useState } from 'react';

import { Form, Formik } from 'formik';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import 'react-phone-number-input/style.css';

import { logException } from '@/utils/logger';
import { getAge } from '@/lib/getAge';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { selectAuthToken } from '@/store/selectors/authSelectors';
import { getUser, getUserTags } from '@/store/actions/user';
import PromptMessage from '@/components/UI/PromptMessage';
import { Button } from '@/components/buttons/Button';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '@/shared/bffApiService';
import { BffApiError } from '@/models/bff-api-error';
import { generateBffErrorMessageWithCode } from '@/utils/utils';
import { DateTime } from 'luxon';
import DateOfBirthField from '@/components/form/date-of-birth/DateOfBirthField';

interface FormValues {
  birthDate: {
    day: string;
    month: string;
    year: string;
  };
}

const initialValues: FormValues = {
  birthDate: {
    day: '',
    month: '',
    year: '',
  },
};

const UserProfileCompletionThailandForm = ({ onSuccess }: { onSuccess?: VoidFunction }) => {
  const [error, setError] = useState('');
  const { request, loading } = useBffApiRequest();
  const token = useSelector(selectAuthToken);
  const intl = useIntl();
  const dispatch = useDispatch();

  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 100;

  const validationSchema = Yup.object().shape({
    birthDate: Yup.object()
      .shape({
        day: Yup.string()
          .required(intl.formatMessage({ id: 'input.error.required' }))
          .test(
            'valid-day',
            intl.formatMessage({ id: 'inputs.date.day.invalid' }),
            function (value) {
              const month = this.parent.month;
              const year = this.parent.year;
              if (!value || !month || !year) {
                return true; // Skip validation if any field is missing
              }
              const numericDay = parseInt(value);
              const daysInMonth = new Date(parseInt(year), parseInt(month), 0).getDate();
              return numericDay <= daysInMonth && numericDay > 0;
            },
          ),
        month: Yup.string()
          .required(intl.formatMessage({ id: 'input.error.required' }))
          .matches(/^(0?[1-9]|1[0-2])$/, intl.formatMessage({ id: 'inputs.date.month.invalid' })),
        year: Yup.string()
          .required(intl.formatMessage({ id: 'input.error.required' }))
          .matches(/^(19|20)\d\d$/, intl.formatMessage({ id: 'inputs.date.year.invalid' }))
          .test('is-valid-year', 'Invalid year range', value => {
            const numericYear = parseInt(value ?? '', 10);
            return numericYear >= minYear && numericYear <= currentYear;
          }),
      })
      .test(
        'is-18-plus',
        intl.formatMessage({ id: 'inputs.birthdate.invalid' }),
        function ({ day, month, year }) {
          if (!day || !month || !year) {
            return false;
          }
          const birthDate = DateTime.local(parseInt(year), parseInt(month), parseInt(day));
          const age = DateTime.now().diff(birthDate, 'years').years;
          return age >= 18;
        },
      ),
  });

  const handleOnSubmit = async (values: FormValues) => {
    setError('');

    const birthday = `${values.birthDate.year}-${values.birthDate.month}-${values.birthDate.day}`;
    const age = getAge(birthday);

    if (age < 18) {
      setError(intl.formatMessage({ id: 'completeProfile.over18' }));
      return;
    }

    const handleSuccess = () => {
      dispatch(getUser(token));
      dispatch(getUserTags(token));
      triggerGTag(GTagEvents.complete_profile_success);
      onSuccess?.();
    };

    const handleError = (error: BffApiError) => {
      const errorMessage = generateBffErrorMessageWithCode(
        intl.formatMessage({
          id: 'error.support',
        }),
        error.code,
      );
      setError(errorMessage);
      triggerGTag(GTagEvents.complete_profile_failed, {
        error: `bff-error-${error.code}`,
      });
      logException(error);
    };

    request({
      apiMethod: BffApiService.completeProfileThailand,
      payload: {
        dob: birthday,
      },
      successCallback: handleSuccess,
      errorCallback: handleError,
    });
    triggerGTag(GTagEvents.complete_profile_submit_click);
  };

  return (
    <div className="flex flex-col justify-center items-center" data-cy="e2e_completeProfileFormTh">
      <div className="text-xl leading-[1] mb-6 opacity-80">
        {intl.formatMessage({ id: 'completeProfile.header' })}
      </div>
      <p className="text-center">{intl.formatMessage({ id: 'completeProfile.text' })}</p>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
        validateOnMount
      >
        {({ isValid }) => (
          <Form className="w-full flex flex-col gap-8">
            <DateOfBirthField
              label={intl.formatMessage({ id: 'inputs.birthdate' })}
              name="birthDate"
              className="border-[1px] border-gray-200 focus-within:border-blue-blue text-black
              [&:focus-within_input]:placeholder:!text-black/60"
            />
            {error && (
              <div className="mt-8">
                <PromptMessage type="error" message={error} />
              </div>
            )}
            <Button
              type="submit"
              disabled={!isValid}
              isLoading={loading}
              text={intl.formatMessage({ id: 'misc.submit' })}
            />
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default UserProfileCompletionThailandForm;
